import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { User, Save, CheckCircle, Zap, HelpCircle, BarChart3, TrendingUp, TrendingDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { user, checkAuth } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [title, setTitle] = useState(user?.title || '');
  const [organization, setOrganization] = useState(user?.organization || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [personality, setPersonality] = useState(user?.coach_personality || 'direct');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [assessment, setAssessment] = useState(null);

  useEffect(() => {
    const fetchAssessment = async () => {
      try {
        const { data } = await axios.get(`${API}/assessment/results`, { withCredentials: true });
        if (data.completed) setAssessment(data);
      } catch (e) { console.error(e); }
    };
    fetchAssessment();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    try {
      await axios.put(`${API}/profile`, { name, title, organization, bio, coach_personality: personality }, { withCredentials: true });
      await checkAuth();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="profile-page" className="p-6 md:p-8 lg:p-12 max-w-3xl">
      <div className="mb-8">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF3B30] mb-2">Profile</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Your Profile</h1>
        <p className="text-[#A3A3A3] text-sm mt-1">Manage your coaching profile and preferences</p>
      </div>

      {/* Avatar & Info */}
      <div className="bg-[#141414] border border-[#262626] rounded-sm p-6 mb-6 flex items-center gap-5">
        <div className="w-16 h-16 bg-[#FF3B30]/10 rounded-full flex items-center justify-center">
          <User className="w-8 h-8 text-[#FF3B30]" />
        </div>
        <div>
          <p className="text-white font-semibold text-lg">{user?.name}</p>
          <p className="text-[#737373] text-sm">{user?.email}</p>
          <p className="text-[#A3A3A3] text-xs mt-1">
            Week {user?.current_week || 1} of 24 | {(user?.completed_modules || []).length} modules completed
          </p>
        </div>
      </div>

      {/* Assessment Results */}
      {assessment && (
        <div className="bg-[#141414] border border-[#262626] rounded-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-[#FF3B30]" />
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#A3A3A3]">Assessment Results</span>
            <span className="ml-auto text-white font-bold text-lg">{assessment.overall_score}/5</span>
          </div>
          <div className="space-y-3">
            {Object.entries(assessment.dimension_scores || {}).map(([dim, score]) => {
              const isStrength = assessment.strengths?.includes(dim);
              const isGrowth = assessment.growth_areas?.includes(dim);
              // Calculate growth from baseline to latest
              const gd = assessment.growth_data;
              let growthVal = null;
              if (gd && gd.baseline && (gd.week_12 || gd.week_24)) {
                const latest = gd.week_24 || gd.week_12;
                const bl = gd.baseline[dim] || 0;
                const lt = latest[dim] || 0;
                growthVal = Math.round((lt - bl) * 10) / 10;
              }
              return (
                <div key={dim}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[#A3A3A3] text-xs flex items-center gap-2">
                      {dim}
                      {isStrength && <span className="text-[#22C55E] text-[10px] font-bold">STRENGTH</span>}
                      {isGrowth && <span className="text-[#F59E0B] text-[10px] font-bold">GROWTH</span>}
                    </span>
                    <span className="text-white text-xs font-bold flex items-center gap-2">
                      {score}/5
                      {growthVal !== null && growthVal !== 0 && (
                        <span className={`flex items-center gap-0.5 text-[10px] ${growthVal > 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
                          {growthVal > 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                          {growthVal > 0 ? '+' : ''}{growthVal}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${isStrength ? 'bg-[#22C55E]' : isGrowth ? 'bg-[#F59E0B]' : 'bg-[#FF3B30]'}`}
                      style={{ width: `${(score / 5) * 100}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSave} className="bg-[#141414] border border-[#262626] rounded-sm p-6 space-y-4">
        {saved && (
          <div data-testid="profile-saved" className="bg-[#22C55E]/10 border border-[#22C55E]/30 text-[#22C55E] text-sm rounded-sm p-3 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" /> Profile saved successfully
          </div>
        )}

        <div>
          <label className="text-[#A3A3A3] text-xs tracking-[0.2em] uppercase font-bold block mb-2">Full Name</label>
          <input
            data-testid="profile-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-3 text-white placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#FF3B30] focus:border-[#FF3B30] transition-all"
          />
        </div>

        <div>
          <label className="text-[#A3A3A3] text-xs tracking-[0.2em] uppercase font-bold block mb-2">Job Title</label>
          <input
            data-testid="profile-title-input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Chief Risk Officer"
            className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-3 text-white placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#FF3B30] focus:border-[#FF3B30] transition-all"
          />
        </div>

        <div>
          <label className="text-[#A3A3A3] text-xs tracking-[0.2em] uppercase font-bold block mb-2">Organization</label>
          <input
            data-testid="profile-org-input"
            type="text"
            value={organization}
            onChange={(e) => setOrganization(e.target.value)}
            placeholder="e.g., Acme Corp"
            className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-3 text-white placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#FF3B30] focus:border-[#FF3B30] transition-all"
          />
        </div>

        <div>
          <label className="text-[#A3A3A3] text-xs tracking-[0.2em] uppercase font-bold block mb-2">Bio</label>
          <textarea
            data-testid="profile-bio-input"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Tell us about your risk management background..."
            rows={4}
            className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-3 text-white placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#FF3B30] focus:border-[#FF3B30] transition-all resize-none"
          />
        </div>

        {/* Coach Personality */}
        <div>
          <label className="text-[#A3A3A3] text-xs tracking-[0.2em] uppercase font-bold block mb-3">Coach Personality</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="profile-personality-direct"
              onClick={() => setPersonality('direct')}
              className={`p-4 rounded-sm border text-left transition-all
                ${personality === 'direct'
                  ? 'border-[#FF3B30] bg-[#FF3B30]/10'
                  : 'border-[#262626] hover:border-[#FF3B30]/50'
                }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Zap className={`w-4 h-4 ${personality === 'direct' ? 'text-[#FF3B30]' : 'text-[#737373]'}`} />
                <span className={`font-bold text-sm ${personality === 'direct' ? 'text-white' : 'text-[#A3A3A3]'}`}>Direct</span>
              </div>
              <p className="text-[#737373] text-xs leading-relaxed">Clear, assertive, action-oriented. Challenges you directly and provides concrete frameworks.</p>
            </button>
            <button
              type="button"
              data-testid="profile-personality-curious"
              onClick={() => setPersonality('curious')}
              className={`p-4 rounded-sm border text-left transition-all
                ${personality === 'curious'
                  ? 'border-[#FF3B30] bg-[#FF3B30]/10'
                  : 'border-[#262626] hover:border-[#FF3B30]/50'
                }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <HelpCircle className={`w-4 h-4 ${personality === 'curious' ? 'text-[#FF3B30]' : 'text-[#737373]'}`} />
                <span className={`font-bold text-sm ${personality === 'curious' ? 'text-white' : 'text-[#A3A3A3]'}`}>Curious</span>
              </div>
              <p className="text-[#737373] text-xs leading-relaxed">Socratic, exploratory, reflective. Asks powerful questions to help you discover insights yourself.</p>
            </button>
          </div>
        </div>

        <button
          data-testid="profile-save-button"
          type="submit"
          disabled={saving}
          className="bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold px-6 py-3 rounded-sm transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Profile'}
        </button>
      </form>
    </div>
  );
}
